import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Mail, Shield, Eye, AlertCircle, Settings2, Plus, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_ICONS = {
  admin: Shield,
  member: Users,
  viewer: Eye,
};

export const TeamManagement = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePerms, setNewRolePerms] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [changingRole, setChangingRole] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, rolesRes, permsRes] = await Promise.all([
        axios.get(`${API}/team/members`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/team/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/team/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setMembers(membersRes.data);
      setRoles(rolesRes.data);
      setAllPermissions(permsRes.data);
    } catch {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const roleNames = roles.map(r => r.name);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await axios.post(`${API}/team/invite`, { email: inviteEmail, role: inviteRole }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast.success(`Invitation sent to ${inviteEmail}!`);
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole('member');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from the team?`)) return;
    try {
      await axios.delete(`${API}/team/members/${memberId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Team member removed');
      fetchData();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    setChangingRole(memberId);
    try {
      await axios.patch(`${API}/team/members/${memberId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast.success(`Role updated to ${newRole}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setChangingRole(null);
    }
  };

  const openRoleEditor = (role) => {
    if (role) {
      setEditingRole(role);
      setNewRoleName(role.name);
      setNewRolePerms([...role.permissions]);
    } else {
      setEditingRole(null);
      setNewRoleName('');
      setNewRolePerms([]);
    }
    setShowRoleEditor(true);
  };

  const handleSaveRole = async () => {
    if (!newRoleName.trim()) { toast.error('Role name is required'); return; }
    if (newRolePerms.length === 0) { toast.error('Select at least one permission'); return; }

    try {
      if (editingRole) {
        await axios.patch(`${API}/team/roles/${editingRole.id}`, { permissions: newRolePerms }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        toast.success(`Role "${newRoleName}" updated`);
      } else {
        await axios.post(`${API}/team/roles`, { name: newRoleName, permissions: newRolePerms }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        toast.success(`Role "${newRoleName}" created`);
      }
      setShowRoleEditor(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    const isBuiltin = ['admin', 'member', 'viewer'].includes(roleName);
    const msg = isBuiltin ? `Reset "${roleName}" to default permissions?` : `Delete role "${roleName}"?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API}/team/roles/${roleId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isBuiltin ? `"${roleName}" reset to defaults` : `"${roleName}" deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePerm = (perm) => {
    setNewRolePerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const RoleIcon = ({ role }) => {
    const Icon = ROLE_ICONS[role] || Settings2;
    return <Icon className="h-4 w-4" aria-hidden="true" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading team...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />

      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="team-title">
                Team Management
              </h1>
              <p className="text-[#475569]">Manage members, roles, and permissions</p>
            </div>
            <div className="flex gap-2">
              <div className="flex flex-col items-end">
                <Button
                  disabled
                  className="rounded-full bg-[#94A3B8] cursor-not-allowed opacity-70"
                  title="Email invitations coming soon"
                  data-testid="invite-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
                <span className="text-[10px] text-[#94A3B8] mt-1" data-testid="invite-coming-soon">
                  Coming soon (to be implemented)
                </span>
              </div>
            </div>
          </div>

          {/* Roles Section */}
          <section className="mb-10" data-testid="roles-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Roles & Permissions</h2>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => openRoleEditor(null)} data-testid="create-role-btn">
                <Plus className="h-4 w-4 mr-1" /> New Role
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map(role => (
                <Card key={role.id} className="p-5 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-colors" data-testid={`role-card-${role.name}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <RoleIcon role={role.name} />
                      <h3 className="font-bold capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>{role.name}</h3>
                      {role.builtin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1d4ed8] font-medium">Built-in</span>
                      )}
                      {role.customized && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">Customized</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openRoleEditor(role)} className="p-1 hover:bg-[#F1F5F9] rounded"
                        aria-label={`Edit ${role.name} role`} data-testid={`edit-role-${role.name}`}>
                        <Settings2 className="h-3.5 w-3.5 text-[#475569]" />
                      </button>
                      {(role.customized || !role.builtin) && (
                        <button onClick={() => handleDeleteRole(role.id, role.name)} className="p-1 hover:bg-red-50 rounded"
                          aria-label={`Delete ${role.name} role`} data-testid={`delete-role-${role.name}`}>
                          <X className="h-3.5 w-3.5 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {role.permissions.map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">
                        {p.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-2">{role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}</p>
                </Card>
              ))}
            </div>
          </section>

          {/* Role Editor Dialog */}
          <Dialog open={showRoleEditor} onOpenChange={setShowRoleEditor}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingRole ? `Edit Role: ${editingRole.name}` : 'Create New Role'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="role-name">Role Name</Label>
                  <Input id="role-name" value={newRoleName} onChange={e => setNewRoleName(e.target.value)}
                    disabled={editingRole?.builtin} placeholder="e.g. editor, analyst" className="mt-1"
                    data-testid="role-name-input" />
                  {editingRole?.builtin && <p className="text-xs text-[#94A3B8] mt-1">Built-in role names cannot be changed</p>}
                </div>
                <div>
                  <Label>Permissions ({newRolePerms.length}/{allPermissions.length})</Label>
                  <div className="grid grid-cols-1 gap-2 mt-2 max-h-[300px] overflow-y-auto" data-testid="permission-list">
                    {allPermissions.map(perm => (
                      <button key={perm.id} onClick={() => togglePerm(perm.id)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all text-sm ${
                          newRolePerms.includes(perm.id)
                            ? 'border-[#1d4ed8] bg-[#EFF6FF]'
                            : 'border-[#E2E8F0] hover:border-[#CBD5E1]'
                        }`}
                        data-testid={`perm-toggle-${perm.id}`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${
                          newRolePerms.includes(perm.id) ? 'bg-[#1d4ed8]' : 'bg-[#E2E8F0]'
                        }`}>
                          {newRolePerms.includes(perm.id) && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <div>
                          <p className="font-medium text-[#1E293B]">{perm.id.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-[#64748B]">{perm.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveRole} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]" data-testid="save-role-btn">
                    {editingRole ? 'Save Changes' : 'Create Role'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowRoleEditor(false)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Members Section */}
          <section data-testid="members-section">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Team Members ({members.length})
            </h2>

            {members.length === 0 ? (
              <Card className="p-12 text-center border border-[#E2E8F0]">
                <Users className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
                <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>No team members yet</h3>
                <p className="text-[#475569] mb-6">Invite colleagues to collaborate on your forms</p>
                <Button onClick={() => setShowInviteDialog(true)} className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]">
                  <UserPlus className="h-4 w-4 mr-2" /> Invite Your First Member
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {members.map((member, index) => (
                  <motion.div key={member.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}>
                    <Card className="p-5 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-colors" data-testid={`member-card-${member.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-[#1d4ed8] flex items-center justify-center shrink-0">
                            <span className="text-white font-bold">{member.email.charAt(0).toUpperCase()}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{member.email}</h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                                member.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {member.status === 'active' ? 'Active' : 'Pending'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-xs text-[#475569]">
                              <Mail className="h-3 w-3" />
                              <span>
                                {member.status === 'pending'
                                  ? `Invited ${new Date(member.invited_at).toLocaleDateString()}`
                                  : `Joined ${new Date(member.joined_at).toLocaleDateString()}`}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          {/* Inline role selector */}
                          <select
                            value={member.role}
                            onChange={e => handleRoleChange(member.id, e.target.value)}
                            disabled={changingRole === member.id}
                            className="text-sm border border-[#E2E8F0] rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-[#1d4ed8] bg-white capitalize"
                            data-testid={`role-select-${member.id}`}
                            aria-label={`Change role for ${member.email}`}
                          >
                            {roleNames.map(r => (
                              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                            ))}
                          </select>

                          <Button size="sm" variant="ghost" onClick={() => handleRemove(member.id, member.email)}
                            className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            aria-label={`Remove ${member.email}`} data-testid={`remove-member-${member.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
